export type YoutubeReplayPlayer = {
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  mute: () => void;
  unMute: () => void;
  destroy: () => void;
};

type YoutubeApi = {
  Player: new (
    element: HTMLElement | string,
    options: {
      videoId: string;
      playerVars?: Record<string, number | string>;
      events?: {
        onReady?: () => void;
        onStateChange?: (event: { data: number }) => void;
      };
    },
  ) => YoutubeReplayPlayer;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number };
};

// On n'augmente pas le type global Window (déjà déclaré ailleurs avec une autre forme de YoutubeApi) :
// on accède à window via un cast local pour rester indépendant.
type YoutubeWindow = {
  YT?: YoutubeApi;
  onYouTubeIframeAPIReady?: () => void;
};

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

let iframeApiPromise: Promise<YoutubeApi> | null = null;

export function loadYoutubeIframeApi(): Promise<YoutubeApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window unavailable'));
  }
  const youtubeWindow = window as unknown as YoutubeWindow;
  if (youtubeWindow.YT?.Player) {
    return Promise.resolve(youtubeWindow.YT);
  }
  if (iframeApiPromise) {
    return iframeApiPromise;
  }

  iframeApiPromise = new Promise<YoutubeApi>((resolve) => {
    const previousCallback = youtubeWindow.onYouTubeIframeAPIReady;
    youtubeWindow.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      if (youtubeWindow.YT) resolve(youtubeWindow.YT);
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${IFRAME_API_SRC}"]`);
    if (!existing) {
      const script = document.createElement('script');
      script.src = IFRAME_API_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return iframeApiPromise;
}
